<template>
  <div class="item-management">
    <div class="page-header">
      <h1 class="page-title">游戏道具管理</h1>
      <p class="page-description">管理游戏道具图标，上传JPG/PNG格式的道具图标文件</p>
    </div>

    <div class="content-container">
      <!-- 工具栏 -->
      <div class="toolbar">
        <div class="toolbar-left">
          <div class="game-selector">
            <label for="gameSelect">选择游戏：</label>
            <select 
              id="gameSelect" 
              v-model="selectedGame" 
              @change="loadGameItems"
              class="game-select"
            >
              <option value="尤弥尔传奇国际服">尤弥尔传奇国际服</option>
              <option value="尤弥尔传奇韩服">尤弥尔传奇韩服</option>
            </select>
          </div>
        </div>
        <div class="toolbar-right">
          <button 
            class="upload-btn" 
            @click="showUploadDialog = true"
            :disabled="!selectedGame"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="17,8 12,3 7,8"/>
              <line x1="12" y1="3" x2="12" y2="15"/>
            </svg>
            上传道具图标
          </button>
        </div>
      </div>

      <!-- 道具列表区域 -->
      <div class="items-section">
        <div class="section-header">
          <h2 class="section-title">{{ selectedGame }} - 道具列表</h2>
          <div class="selection-controls">
            <div class="selection-info">
              <span class="selected-count">已选择: {{ selectedItems.length }} / {{ gameItems.length }}</span>
              <button
                class="link-btn"
                @click="toggleSelectAll"
              >
                {{ isAllSelected ? '取消全选' : '全选' }}
              </button>
            </div>
            <div class="category-selections">
              <button
                class="category-btn"
                @click="selectByCategory('warehouse')"
                :disabled="gameItems.length === 0"
              >
                选择存仓道具
              </button>
              <button
                class="category-btn"
                @click="selectByCategory('delete')"
                :disabled="gameItems.length === 0"
              >
                选择垃圾道具
              </button>
              <button
                class="category-btn"
                @click="selectByCategory('use')"
                :disabled="gameItems.length === 0"
              >
                选择使用道具
              </button>
              <button
                class="category-btn"
                @click="clearSelection"
                :disabled="selectedItems.length === 0"
              >
                清空选择
              </button>
            </div>
          </div>
          <div class="section-actions">
            <button
              class="action-btn secondary"
              @click="batchToggleStatus"
              :disabled="selectedItems.length === 0"
            >
              批量启用/禁用
            </button>
            <button
              class="action-btn secondary"
              @click="batchToggleWarehouse"
              :disabled="selectedItems.length === 0"
            >
              批量仓库可见
            </button>
            <button
              class="action-btn secondary"
              @click="batchToggleDelete"
              :disabled="selectedItems.length === 0"
            >
              批量删除可见
            </button>
            <button
              class="action-btn secondary"
              @click="batchToggleUse"
              :disabled="selectedItems.length === 0"
            >
              批量使用可见
            </button>
            <button
              class="action-btn danger"
              @click="batchDeleteItems"
              :disabled="selectedItems.length === 0"
            >
              批量删除
            </button>
          </div>
        </div>

        <!-- 调试信息 -->
        <div v-if="gameItems.length === 0" style="padding: 20px; text-align: center; color: #666;">
          调试信息：gameItems.length = {{ gameItems.length }}
          <br>
          gameItems = {{ JSON.stringify(gameItems) }}
        </div>
        
        <!-- 道具表格 -->
        <div class="items-table" v-if="gameItems.length > 0">
          <table>
            <thead>
              <tr>
                <th>
                  <input 
                    type="checkbox" 
                    @change="toggleSelectAll"
                    :checked="isAllSelected"
                  >
                </th>
                <th>道具图标</th>
                <th>道具名称</th>
                <th>启用状态</th>
                <th>仓库可见</th>
                <th>删除可见</th>
                <th>使用可见</th>
                <th>背景色</th>
                <th>创建时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in gameItems" :key="item.id">
                <td>
                  <input 
                    type="checkbox" 
                    :value="item.id"
                    v-model="selectedItems"
                  >
                </td>
                <td>
                  <div class="item-icon" :class="getBackgroundColorClass(item.background_color)">
                    <img 
                      :src="getItemIconUrl(item)" 
                      :alt="item.item_name"
                      @error="handleImageError"
                    >
                  </div>
                </td>
                <td class="item-name">
                  <div 
                    v-if="editingItemId !== item.id"
                    class="item-name-display"
                    @dblclick="startEditItemName(item)"
                    :title="'双击编辑道具名称'"
                  >
                    {{ item.item_name }}
                  </div>
                  <div v-else class="item-name-edit">
                    <input
                      ref="editNameInput"
                      v-model="editingItemName"
                      @blur="saveItemName(item)"
                      @keyup.enter="saveItemName(item)"
                      @keyup.escape="cancelEditItemName"
                      class="edit-name-input"
                      :placeholder="item.item_name"
                    />
                  </div>
                </td>
                <td>
                  <button 
                    class="status-btn"
                    :class="{ active: item.status }"
                    @click="toggleItemStatus(item)"
                  >
                    {{ item.status ? '已启用' : '已禁用' }}
                  </button>
                </td>
                <td>
                  <button 
                    class="status-btn"
                    :class="{ active: item.is_warehouse }"
                    @click="toggleItemWarehouse(item)"
                  >
                    {{ item.is_warehouse ? '可见' : '不可见' }}
                  </button>
                </td>
                <td>
                  <button 
                    class="status-btn"
                    :class="{ active: item.is_delete }"
                    @click="toggleItemDelete(item)"
                  >
                    {{ item.is_delete ? '可见' : '不可见' }}
                  </button>
                </td>
                <td>
                  <button 
                    class="status-btn"
                    :class="{ active: item.is_use }"
                    @click="toggleItemUse(item)"
                  >
                    {{ item.is_use ? '可见' : '不可见' }}
                  </button>
                </td>
                <td>
                  <select 
                    v-model="item.background_color" 
                    class="background-color-select"
                    @change="updateItemBackgroundColor(item)"
                  >
                    <option :value="null">无</option>
                    <option value="light-green">浅绿</option>
                    <option value="light-blue">浅蓝</option>
                    <option value="light-purple">浅紫</option>
                    <option value="light-gold">金色</option>
                  </select>
                </td>
                <td class="created-time">{{ formatDate(item.created_at) }}</td>
                <td>
                  <div class="action-buttons">
                    <button 
                      class="copy-btn"
                      @click="copyItem(item)"
                    >
                      复制
                    </button>
                    <button 
                      class="delete-btn"
                      @click="deleteItem(item)"
                    >
                      删除
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- 空状态 -->
        <div class="empty-state" v-else>
          <div class="empty-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
            </svg>
          </div>
          <h3 class="empty-title">暂无道具数据</h3>
          <p class="empty-description">请上传道具图标文件</p>
        </div>
      </div>


    </div>

    <!-- 上传对话框 -->
    <div class="upload-dialog" v-if="showUploadDialog" @click.self="closeUploadDialog">
      <div class="dialog-content">
        <div class="dialog-header">
          <h3>上传道具图标</h3>
          <button class="close-btn" @click="closeUploadDialog">×</button>
        </div>
        <div class="dialog-body">
          <div class="upload-area" :class="{ dragover: isDragOver }" 
               @drop="handleDrop" 
               @dragover.prevent="isDragOver = true"
               @dragleave="isDragOver = false">
            <div class="upload-content">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17,8 12,3 7,8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              <p>拖拽图片文件到此处或点击选择</p>
              <p class="upload-hint">支持 JPG、PNG 格式，文件名即为道具名称</p>
              <input 
                type="file" 
                ref="fileInput"
                multiple
                accept=".jpg,.jpeg,.png"
                @change="handleFileSelect"
                style="display: none;"
              >
              <button class="select-btn" @click="$refs.fileInput.click()">
                选择文件
              </button>
            </div>
          </div>
          
          <!-- 上传进度 -->
          <div class="upload-progress" v-if="uploadFiles.length > 0">
            <h4>上传进度</h4>
            <div class="file-list">
              <div 
                v-for="file in uploadFiles" 
                :key="file.name"
                class="file-item"
                :class="{ 
                  success: file.status === 'success',
                  error: file.status === 'error',
                  uploading: file.status === 'uploading'
                }"
              >
                <div class="file-info">
                  <span class="file-name">{{ file.name }}</span>
                  <div class="file-background-color-selector">
                    <label>背景色：</label>
                    <select 
                      v-model="file.backgroundColor" 
                      class="background-color-select"
                      :disabled="file.status === 'uploading' || file.status === 'success'"
                    >
                      <option :value="null">无</option>
                      <option value="light-green">浅绿</option>
                      <option value="light-blue">浅蓝</option>
                      <option value="light-purple">浅紫</option>
                    </select>
                  </div>
                </div>
                <span class="file-status">{{ getFileStatusText(file.status) }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="dialog-footer">
          <button class="cancel-btn" @click="closeUploadDialog">取消</button>
          <button 
            class="upload-submit-btn" 
            @click="startUpload"
            :disabled="selectedFiles.length === 0 || isUploading"
          >
            {{ isUploading ? '上传中...' : '开始上传' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, nextTick } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { gameItemsAPI } from '@/api/gameItems'

export default {
  name: 'ItemManagement',
  setup() {
    // 响应式数据
    const selectedGame = ref('尤弥尔传奇国际服')
    const gameItems = ref([])
    const selectedItems = ref([])
    const showUploadDialog = ref(false)
    const selectedFiles = ref([])
    const uploadFiles = ref([])
    const isDragOver = ref(false)
    const isUploading = ref(false)
    const editingItemId = ref(null)
    const editingItemName = ref('')

    // 计算属性
    const isAllSelected = computed(() => {
      return gameItems.value.length > 0 && selectedItems.value.length === gameItems.value.length
    })

    // 加载游戏道具列表
    const loadGameItems = async () => {
      if (!selectedGame.value) {
        gameItems.value = []
        return
      }
      
      try {
        console.log('加载游戏道具:', selectedGame.value)
        const response = await gameItemsAPI.getGameItems(selectedGame.value)
        // 后端直接返回数组在 data 字段中
        let items = response.data.data || response.data || []
        
        // 按背景色排序：白色 → 绿色 → 蓝色 → 紫色 → 金色
        items = items.sort((a, b) => {
          const colorOrder = {
            '': 0,           // 白色（无背景色）
            null: 0,
            undefined: 0,
            'light-green': 1, // 绿色
            'light-blue': 2,  // 蓝色
            'light-purple': 3, // 紫色
            'light-gold': 4   // 金色
          }
          const orderA = colorOrder[a.background_color] ?? 0
          const orderB = colorOrder[b.background_color] ?? 0
          return orderA - orderB
        })
        
        gameItems.value = items
        selectedItems.value = []
        console.log('道具列表加载成功，数量:', gameItems.value.length)
        console.log('道具列表数据:', gameItems.value)
      } catch (error) {
        console.error('加载道具列表失败:', error)
        gameItems.value = []
        selectedItems.value = []
        ElMessage.error('加载道具列表失败: ' + (error.response?.data?.message || error.message))
      }
    }

    // 文件选择处理
    const handleFileSelect = (event) => {
      const files = Array.from(event.target.files)
      selectedFiles.value = files
      uploadFiles.value = files.map(file => ({
        name: file.name.replace(/\.(jpg|jpeg|png)$/i, ''), // 去掉扩展名
        file: file,
        status: 'pending',
        backgroundColor: null // 默认无背景色
      }))
    }

    // 拖拽文件处理
    const handleDrop = (event) => {
      event.preventDefault()
      isDragOver.value = false
      const files = Array.from(event.dataTransfer.files).filter(file => 
        /\.(jpg|jpeg|png)$/i.test(file.name)
      )
      selectedFiles.value = files
      uploadFiles.value = files.map(file => ({
        name: file.name.replace(/\.(jpg|jpeg|png)$/i, ''),
        file: file,
        status: 'pending',
        backgroundColor: null // 默认无背景色
      }))
    }

    // 开始上传
    const startUpload = async () => {
      if (selectedFiles.value.length === 0) return
      
      isUploading.value = true
      
      try {
        // 标记所有文件为上传中
        uploadFiles.value.forEach(fileInfo => {
          fileInfo.status = 'uploading'
        })
        
        console.log('开始批量上传文件:', selectedFiles.value.length, '个')
        
        // 调用批量上传API
        const response = await gameItemsAPI.uploadItemIcons(selectedGame.value, uploadFiles.value)
        
        console.log('上传响应:', response.data)
        
        // 检查响应格式，兼容两种可能的返回格式
        let result;
        if (response.data.code === 200) {
          // 标准API响应格式
          result = response.data.data;
        } else if (response.data.success_count !== undefined) {
          // 直接返回数据格式
          result = response.data;
        } else {
          throw new Error('无效的响应格式');
        }
        
        const successCount = result.success_count || 0
        const failedCount = result.failed_count || 0
        
        // 更新文件状态
        uploadFiles.value.forEach(fileInfo => {
          const originalName = fileInfo.file.name
          console.log('检查文件状态:', originalName)
          console.log('成功列表:', Object.keys(result.success_items || {}))
          console.log('失败列表:', Object.keys(result.failed_items || {}))
          
          if (result.success_items && result.success_items[originalName]) {
            fileInfo.status = 'success'
            console.log('标记为成功:', originalName)
          } else if (result.failed_items && result.failed_items[originalName]) {
            fileInfo.status = 'error'
            console.log('标记为失败:', originalName)
          } else {
            // 如果既不在成功列表也不在失败列表，根据整体结果判断
            if (failedCount === 0 && successCount > 0) {
              fileInfo.status = 'success'
              console.log('根据整体结果标记为成功:', originalName)
            } else {
              fileInfo.status = 'error'
              console.log('未找到匹配，标记为失败:', originalName)
            }
          }
        })
        
        if (failedCount === 0) {
          ElMessage.success(`所有文件上传成功！共 ${successCount} 个文件`)
        } else {
          ElMessage.warning(`上传完成！成功 ${successCount} 个，失败 ${failedCount} 个`)
        }
        
      } catch (error) {
        console.error('批量上传失败:', error)
        
        // 标记所有文件为失败
        uploadFiles.value.forEach(fileInfo => {
          if (fileInfo.status === 'uploading') {
            fileInfo.status = 'error'
          }
        })
        
        ElMessage.error('上传失败: ' + (error.response?.data?.message || error.message))
      }
      
      isUploading.value = false
      
      // 上传完成后刷新列表
      await loadGameItems()
    }

    // 获取文件扩展名
    const getFileExtension = (filename) => {
      return filename.split('.').pop().toLowerCase()
    }

    // 获取文件状态文本
    const getFileStatusText = (status) => {
      const statusMap = {
        pending: '等待上传',
        uploading: '上传中...',
        success: '上传成功',
        error: '上传失败'
      }
      return statusMap[status] || '未知状态'
    }

    // 关闭上传对话框
    const closeUploadDialog = () => {
      showUploadDialog.value = false
      selectedFiles.value = []
      uploadFiles.value = []
    }

    // 获取道具图标URL
    const getItemIconUrl = (item) => {
      // 后端返回的数据中已包含预签名URL
      return item.icon_url || null
    }

    // 处理图片加载错误
    const handleImageError = (event) => {
      event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB4PSIzIiB5PSIzIiB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHJ4PSIyIiByeT0iMiIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTAiIGZpbGw9IiM5Y2EzYWYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj7plJnor689L3RleHQ+PC9zdmc+'
    }

    // 格式化日期
    const formatDate = (dateString) => {
      if (!dateString) return ''
      return new Date(dateString).toLocaleString('zh-CN')
    }

    // 切换单个道具状态
    const toggleItemStatus = async (item) => {
      try {
        const newStatus = item.status ? 0 : 1
        console.log('切换道具状态:', item.id, newStatus)
        await gameItemsAPI.updateItemStatus(item.id, newStatus)
        item.status = newStatus
        ElMessage.success('状态更新成功')
      } catch (error) {
        console.error('更新状态失败:', error)
        ElMessage.error('状态更新失败: ' + (error.response?.data?.message || error.message))
      }
    }

    // 切换仓库可见状态
    const toggleItemWarehouse = async (item) => {
      try {
        const newWarehouse = item.is_warehouse ? 0 : 1
        console.log('切换仓库可见:', item.id, newWarehouse)
        await gameItemsAPI.updateItemWarehouse(item.id, newWarehouse)
        item.is_warehouse = newWarehouse
        ElMessage.success('仓库可见状态更新成功')
      } catch (error) {
        console.error('更新仓库可见状态失败:', error)
        ElMessage.error('仓库可见状态更新失败: ' + (error.response?.data?.message || error.message))
      }
    }

    // 切换删除可见状态
    const toggleItemDelete = async (item) => {
      try {
        const newDelete = item.is_delete ? 0 : 1
        console.log('切换删除可见:', item.id, newDelete)
        await gameItemsAPI.updateItemDelete(item.id, newDelete)
        item.is_delete = newDelete
        ElMessage.success('删除可见状态更新成功')
      } catch (error) {
        console.error('更新删除可见状态失败:', error)
        ElMessage.error('删除可见状态更新失败: ' + (error.response?.data?.message || error.message))
      }
    }

    // 切换使用可见状态
    const toggleItemUse = async (item) => {
      try {
        const newUse = item.is_use ? 0 : 1
        console.log('切换使用可见:', item.id, newUse)
        await gameItemsAPI.updateItemUse(item.id, newUse)
        item.is_use = newUse
        ElMessage.success('使用可见状态更新成功')
      } catch (error) {
        console.error('更新使用可见状态失败:', error)
        ElMessage.error('使用可见状态更新失败: ' + (error.response?.data?.message || error.message))
      }
    }

    // 更新道具背景色
    const updateItemBackgroundColor = async (item) => {
      try {
        console.log('更新背景色:', item.id, item.background_color)
        await gameItemsAPI.updateItemBackgroundColor(item.id, item.background_color)
        ElMessage.success('背景色更新成功')
      } catch (error) {
        console.error('更新背景色失败:', error)
        ElMessage.error('背景色更新失败: ' + (error.response?.data?.message || error.message))
        // 恢复原值
        await loadGameItems()
      }
    }

    // 获取背景色CSS类
    const getBackgroundColorClass = (backgroundColor) => {
      if (!backgroundColor) return ''
      return `bg-${backgroundColor}`
    }

    // 复制单个道具
    const copyItem = async (item) => {
      try {
        console.log('复制道具:', item.id)
        await gameItemsAPI.copyItem(item.id)
        
        // 刷新列表以显示新复制的道具
        await loadGameItems()
        
        ElMessage.success('复制成功')
      } catch (error) {
        console.error('复制失败:', error)
        ElMessage.error('复制失败: ' + (error.response?.data?.message || error.message))
      }
    }

    // 删除单个道具
    const deleteItem = async (item) => {
      try {
        await ElMessageBox.confirm(`确定要删除道具"${item.item_name}"吗？此操作将同时删除OSS文件。`, '确认删除', {
          type: 'warning'
        })
        
        console.log('删除道具:', item.id)
        await gameItemsAPI.deleteItem(item.id)
        
        // 从列表中移除
        const index = gameItems.value.findIndex(g => g.id === item.id)
        if (index > -1) {
          gameItems.value.splice(index, 1)
        }
        
        // 从选中项中移除
        const selectedIndex = selectedItems.value.indexOf(item.id)
        if (selectedIndex > -1) {
          selectedItems.value.splice(selectedIndex, 1)
        }
        
        ElMessage.success('删除成功')
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error)
          ElMessage.error('删除失败: ' + (error.response?.data?.message || error.message))
        }
      }
    }

    // 全选/取消全选
    const toggleSelectAll = () => {
      if (isAllSelected.value) {
        selectedItems.value = []
      } else {
        selectedItems.value = gameItems.value.map(item => item.id)
      }
    }

    // 按类别选择道具
    const selectByCategory = (category) => {
      if (!gameItems.value.length) return

      let itemsToSelect = []

      switch (category) {
        case 'warehouse':
          // 选择仓库可见的道具
          itemsToSelect = gameItems.value
            .filter(item => item.is_warehouse === 1)
            .map(item => item.id)
          break
        case 'delete':
          // 选择删除可见的道具（垃圾道具）
          itemsToSelect = gameItems.value
            .filter(item => item.is_delete === 1)
            .map(item => item.id)
          break
        case 'use':
          // 选择使用可见的道具
          itemsToSelect = gameItems.value
            .filter(item => item.is_use === 1)
            .map(item => item.id)
          break
      }

      // 如果已有选择，则合并选择；否则直接选择
      const currentSelection = new Set(selectedItems.value)
      itemsToSelect.forEach(id => currentSelection.add(id))
      selectedItems.value = Array.from(currentSelection)

      const categoryName = {
        'warehouse': '存仓道具',
        'delete': '垃圾道具',
        'use': '使用道具'
      }[category]

      ElMessage.success(`已选择${itemsToSelect.length}个${categoryName}`)
    }

    // 清空选择
    const clearSelection = () => {
      selectedItems.value = []
      ElMessage.info('已清空所有选择')
    }

    // 批量切换状态
    const batchToggleStatus = async () => {
      if (selectedItems.value.length === 0) return
      
      try {
        // 智能切换：如果选中的都是启用状态则批量禁用，否则批量启用
        const selectedItemsData = gameItems.value.filter(item => selectedItems.value.includes(item.id))
        const allEnabled = selectedItemsData.every(item => item.status === 1)
        const newStatus = allEnabled ? 0 : 1
        
        console.log('批量切换状态:', selectedItems.value, '到', newStatus)
        await gameItemsAPI.batchUpdateStatus(selectedItems.value, newStatus)
        ElMessage.success(`批量${newStatus ? '启用' : '禁用'}成功`)
        await loadGameItems()
      } catch (error) {
        console.error('批量状态更新失败:', error)
        ElMessage.error('批量状态更新失败: ' + (error.response?.data?.message || error.message))
      }
    }

    // 批量切换仓库可见
    const batchToggleWarehouse = async () => {
      if (selectedItems.value.length === 0) return
      
      try {
        // 智能切换：如果选中的都是可见状态则批量隐藏，否则批量显示
        const selectedItemsData = gameItems.value.filter(item => selectedItems.value.includes(item.id))
        const allVisible = selectedItemsData.every(item => item.is_warehouse === 1)
        const newWarehouse = allVisible ? 0 : 1
        
        console.log('批量切换仓库可见:', selectedItems.value, '到', newWarehouse)
        await gameItemsAPI.batchUpdateWarehouse(selectedItems.value, newWarehouse)
        ElMessage.success(`批量仓库${newWarehouse ? '可见' : '隐藏'}成功`)
        await loadGameItems()
      } catch (error) {
        console.error('批量仓库可见状态更新失败:', error)
        ElMessage.error('批量仓库可见状态更新失败: ' + (error.response?.data?.message || error.message))
      }
    }

    // 批量切换删除可见
    const batchToggleDelete = async () => {
      if (selectedItems.value.length === 0) return
      
      try {
        // 智能切换：如果选中的都是可见状态则批量隐藏，否则批量显示
        const selectedItemsData = gameItems.value.filter(item => selectedItems.value.includes(item.id))
        const allVisible = selectedItemsData.every(item => item.is_delete === 1)
        const newDelete = allVisible ? 0 : 1
        
        console.log('批量切换删除可见:', selectedItems.value, '到', newDelete)
        await gameItemsAPI.batchUpdateDelete(selectedItems.value, newDelete)
        ElMessage.success(`批量删除${newDelete ? '可见' : '隐藏'}成功`)
        await loadGameItems()
      } catch (error) {
        console.error('批量删除可见状态更新失败:', error)
        ElMessage.error('批量删除可见状态更新失败: ' + (error.response?.data?.message || error.message))
      }
    }

    // 批量切换使用可见状态
    const batchToggleUse = async () => {
      if (selectedItems.value.length === 0) return
      
      try {
        // 检查选中道具的使用可见状态，如果都是可见则切换为不可见，否则切换为可见
        const selectedGameItems = gameItems.value.filter(item => selectedItems.value.includes(item.id))
        const allVisible = selectedGameItems.every(item => item.is_use === 1)
        const newUse = allVisible ? 0 : 1
        
        console.log('批量切换使用可见:', selectedItems.value, '到', newUse)
        await gameItemsAPI.batchUpdateUse(selectedItems.value, newUse)
        ElMessage.success(`批量使用${newUse ? '可见' : '隐藏'}成功`)
        await loadGameItems()
      } catch (error) {
        console.error('批量使用可见状态更新失败:', error)
        ElMessage.error('批量使用可见状态更新失败: ' + (error.response?.data?.message || error.message))
      }
    }

    // 批量删除道具
    const batchDeleteItems = async () => {
      if (selectedItems.value.length === 0) return
      
      try {
        await ElMessageBox.confirm(`确定要删除选中的 ${selectedItems.value.length} 个道具吗？此操作将同时删除OSS文件。`, '确认批量删除', {
          type: 'warning'
        })
        
        console.log('批量删除道具:', selectedItems.value)
        await gameItemsAPI.batchDelete(selectedItems.value)
        
        ElMessage.success('批量删除成功')
        selectedItems.value = []
        await loadGameItems()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('批量删除失败:', error)
          ElMessage.error('批量删除失败: ' + (error.response?.data?.message || error.message))
        }
      }
    }

    // 开始编辑道具名称
    const startEditItemName = (item) => {
      editingItemId.value = item.id
      editingItemName.value = item.item_name
      
      // 下一帧聚焦输入框
      nextTick(() => {
        const input = document.querySelector('.edit-name-input')
        if (input) {
          input.focus()
          input.select()
        }
      })
    }

    // 保存道具名称
    const saveItemName = async (item) => {
      if (!editingItemName.value || editingItemName.value.trim() === '') {
        ElMessage.warning('道具名称不能为空')
        return
      }

      const newName = editingItemName.value.trim()
      
      // 如果名称没有变化，直接取消编辑
      if (newName === item.item_name) {
        cancelEditItemName()
        return
      }

      try {
        console.log('更新道具名称:', item.id, '从', item.item_name, '到', newName)
        await gameItemsAPI.updateItemName(item.id, newName)
        
        // 更新本地数据
        const itemIndex = gameItems.value.findIndex(i => i.id === item.id)
        if (itemIndex !== -1) {
          gameItems.value[itemIndex].item_name = newName
        }
        
        ElMessage.success('道具名称更新成功')
        cancelEditItemName()
      } catch (error) {
        console.error('更新道具名称失败:', error)
        ElMessage.error('更新道具名称失败: ' + (error.response?.data?.message || error.message))
      }
    }

    // 取消编辑道具名称
    const cancelEditItemName = () => {
      editingItemId.value = null
      editingItemName.value = ''
    }

    // 组件挂载时加载默认游戏的道具列表
    onMounted(() => {
      loadGameItems()
    })

    return {
      selectedGame,
      gameItems,
      selectedItems,
      showUploadDialog,
      selectedFiles,
      uploadFiles,
      isDragOver,
      isUploading,
      editingItemId,
      editingItemName,
      isAllSelected,
      loadGameItems,
      handleFileSelect,
      handleDrop,
      startUpload,
      getFileStatusText,
      closeUploadDialog,
      getItemIconUrl,
      handleImageError,
      formatDate,
      toggleItemStatus,
      toggleItemWarehouse,
      toggleItemDelete,
      toggleItemUse,
      updateItemBackgroundColor,
      getBackgroundColorClass,
      copyItem,
      deleteItem,
      toggleSelectAll,
      selectByCategory,
      clearSelection,
      batchToggleStatus,
      batchToggleWarehouse,
      batchToggleDelete,
      batchToggleUse,
      batchDeleteItems,
      startEditItemName,
      saveItemName,
      cancelEditItemName
    }
  }
}
</script>

<style scoped>
.item-management {
  padding: 24px 24px 32px;
  background: #f8f9fa;
  box-sizing: border-box;
}

.page-header {
  margin-bottom: 32px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0 0 8px 0;
  letter-spacing: -0.02em;
}

.page-description {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

.content-container {
  display: grid;
  gap: 24px;
}

/* 工具栏 */
.toolbar {
  background: white;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.game-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.game-selector label {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  white-space: nowrap;
}

.game-select {
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  color: #374151;
  min-width: 180px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.game-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.upload-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.upload-btn:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.upload-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* 道具列表区域 */
.items-section {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
}

.section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
  gap: 20px;
}

.selection-controls {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-width: 300px;
}

.selection-info {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  background: #f8fafc;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.selected-count {
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
}

.link-btn {
  padding: 4px 8px;
  background: none;
  border: none;
  color: #3b82f6;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: underline;
  transition: color 0.2s ease;
}

.link-btn:hover {
  color: #1d4ed8;
}

.category-selections {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.category-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.category-btn:hover:not(:disabled) {
  background: #e2e8f0;
  border-color: #94a3b8;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.category-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0;
  letter-spacing: -0.01em;
}

.section-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;
  color: #374151;
  white-space: nowrap;
}

.action-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.action-btn.danger {
  color: #dc2626;
  border-color: #dc2626;
}

.action-btn.danger:hover:not(:disabled) {
  background: #dc2626;
  color: white;
}

/* 道具表格 */
.items-table {
  overflow-x: auto;
}

.items-table table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.items-table th,
.items-table td {
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.items-table th {
  background: #f9fafb;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
}

.items-table td {
  color: #6b7280;
}

.item-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
  background: #f3f4f6;
  border: 2px solid #e2e8f0;
}

.item-icon.bg-light-green {
  background: linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%);
  border-color: #81c784;
}

.item-icon.bg-light-blue {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-color: #64b5f6;
}

.item-icon.bg-light-purple {
  background: linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%);
  border-color: #ba68c8;
}

.item-icon.bg-light-gold {
  background: linear-gradient(135deg, #fff9e6 0%, #ffe082 100%);
  border-color: #ffc107;
}

.item-icon img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.background-color-select {
  padding: 4px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  background: white;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s ease;
}

.background-color-select:hover {
  border-color: #9ca3af;
}

.background-color-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.file-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
}

.file-background-color-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.file-background-color-selector label {
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
}

.item-name {
  font-weight: 500;
  color: #374151;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-name-display {
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
  min-height: 24px;
  display: flex;
  align-items: center;
}

.item-name-display:hover {
  background-color: #f3f4f6;
  color: #1f2937;
}

.item-name-edit {
  width: 100%;
}

.edit-name-input {
  width: 100%;
  padding: 4px 8px;
  border: 2px solid #3b82f6;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  background: white;
  outline: none;
  transition: all 0.2s ease;
}

.edit-name-input:focus {
  border-color: #1d4ed8;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.status-btn {
  padding: 4px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #f9fafb;
  color: #6b7280;
}

.status-btn.active {
  background: #10b981;
  border-color: #10b981;
  color: white;
}

.status-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.action-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
}

.copy-btn {
  padding: 4px 8px;
  border: 1px solid #3b82f6;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;
  color: #3b82f6;
}

.copy-btn:hover {
  background: #3b82f6;
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
}

.delete-btn {
  padding: 4px 8px;
  border: 1px solid #dc2626;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;
  color: #dc2626;
}

.delete-btn:hover {
  background: #dc2626;
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(220, 38, 38, 0.2);
}

.created-time {
  font-size: 12px;
  color: #9ca3af;
  white-space: nowrap;
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 64px 24px;
}

.empty-icon {
  width: 80px;
  height: 80px;
  background: #f3f4f6;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  color: #9ca3af;
}

.empty-title {
  font-size: 18px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 8px 0;
  letter-spacing: -0.01em;
}

.empty-description {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

/* 上传对话框 */
.upload-dialog {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.dialog-content {
  background: white;
  border-radius: 16px;
  max-width: 600px;
  width: 100%;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 0;
  margin-bottom: 24px;
}

.dialog-header h3 {
  font-size: 20px;
  font-weight: 600;
  color: #1d1d1f;
  margin: 0;
}

.close-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: #f3f4f6;
  border-radius: 8px;
  font-size: 18px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: #e5e7eb;
  color: #374151;
}

.dialog-body {
  padding: 0 24px;
  max-height: 60vh;
  overflow-y: auto;
}

.upload-area {
  border: 2px dashed #d1d5db;
  border-radius: 12px;
  padding: 48px 24px;
  text-align: center;
  transition: all 0.2s ease;
  margin-bottom: 24px;
}

.upload-area.dragover {
  border-color: #3b82f6;
  background: #eff6ff;
}

.upload-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.upload-content svg {
  color: #9ca3af;
}

.upload-content p {
  margin: 0;
  font-size: 16px;
  color: #374151;
  font-weight: 500;
}

.upload-hint {
  font-size: 14px !important;
  color: #6b7280 !important;
  font-weight: 400 !important;
}

.select-btn {
  padding: 10px 20px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.select-btn:hover {
  background: #2563eb;
  transform: translateY(-1px);
}

/* 上传进度 */
.upload-progress {
  margin-top: 24px;
}

.upload-progress h4 {
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 16px 0;
}

.file-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.file-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #f9fafb;
  border-radius: 8px;
  border-left: 4px solid #e5e7eb;
  transition: all 0.2s ease;
}

.file-item.uploading {
  border-left-color: #3b82f6;
}

.file-item.success {
  border-left-color: #10b981;
  background: #f0fdf4;
}

.file-item.error {
  border-left-color: #dc2626;
  background: #fef2f2;
}

.file-name {
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  flex: 1;
  margin-right: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-status {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
}

.file-item.success .file-status {
  color: #10b981;
}

.file-item.error .file-status {
  color: #dc2626;
}

.file-item.uploading .file-status {
  color: #3b82f6;
}

.dialog-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid #e5e7eb;
  margin-top: 24px;
}

.cancel-btn {
  padding: 10px 20px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;
  color: #374151;
}

.cancel-btn:hover {
  border-color: #9ca3af;
  transform: translateY(-1px);
}

.upload-submit-btn {
  padding: 10px 20px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.upload-submit-btn:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.upload-submit-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .item-management {
    padding: 16px;
  }
  
  .toolbar {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }
  
  .toolbar-left {
    justify-content: center;
  }
  
  .game-select {
    min-width: 200px;
  }
  
  .items-section {
    padding: 20px 16px;
  }
  
  .section-header {
    flex-direction: column;
    gap: 16px;
    align-items: stretch;
  }

  .selection-controls {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    min-width: auto;
  }

  .selection-info {
    justify-content: flex-start;
    margin-bottom: 4px;
  }

  .category-selections {
    justify-content: flex-start;
    flex-wrap: wrap;
    width: 100%;
    gap: 8px;
  }

  .category-btn {
    padding: 6px 12px;
    font-size: 12px;
    min-width: auto;
    flex: 0 0 auto;
  }
  
  .section-actions {
    justify-content: center;
    flex-wrap: wrap;
  }
  
  .upload-dialog {
    padding: 16px;
  }
  
  .dialog-content {
    max-height: 90vh;
  }
  
  .upload-area {
    padding: 32px 16px;
  }
  
  .items-table {
    font-size: 13px;
  }
  
  .items-table th,
  .items-table td {
    padding: 8px 12px;
  }
  
  /* 在小屏幕隐藏背景色和创建时间列，节省空间 */
  .items-table th:nth-child(8), /* 背景色列 */
  .items-table td:nth-child(8),
  .items-table th:nth-child(9), /* 创建时间列 */
  .items-table td:nth-child(9) {
    display: none;
  }
}

@media (max-width: 480px) {
  .section-actions {
    flex-direction: column;
  }

  .action-btn {
    justify-content: center;
  }

  .category-selections {
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }

  .category-btn {
    justify-content: center;
    width: 100%;
    padding: 8px 12px;
  }

  .selection-info {
    text-align: center;
    margin-bottom: 8px;
  }

  .dialog-header {
    padding: 20px 20px 0;
  }

  .dialog-body {
    padding: 0 20px;
  }

  .dialog-footer {
    padding: 20px;
  }
  
  /* 在超小屏幕进一步隐藏列，只保留核心功能 */
  .items-table th:nth-child(5), /* 仓库可见列 */
  .items-table td:nth-child(5),
  .items-table th:nth-child(6), /* 删除可见列 */
  .items-table td:nth-child(6),
  .items-table th:nth-child(7), /* 使用可见列 */
  .items-table td:nth-child(7) {
    display: none;
  }
}
</style>
